/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { getColor } from '@airasia-phoenix/react-native/utils/utils';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'expo-router';
import { useLogSheetLoginSuccess } from '../../hooks/useLogSheetLoginSuccess';
import {
  Modal,
  ScrollView,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import LoginView from '../../widgets/LoginView/LoginView';
import { defaultProps } from '../../widgets/LoginView/types';
import ForgotPasswordView from '../../widgets/ForgotPasswordView/ForgotPasswordView';
import { defaultProps as forgotPasswordDefaultProps } from '../../widgets/ForgotPasswordView/types';
import SignUpView from '../../widgets/SignUpView/SignUpView';
import { defaultProps as signUpDefaultProps } from '../../widgets/SignUpView/types';
import SignUpFormView from '../../widgets/SignUpFormView/SignUpFormView';
import { defaultProps as signUpFormDefaultProps } from '../../widgets/SignUpFormView/types';
import VerifyPhoneView from '../../widgets/VerifyPhoneView/VerifyPhoneView';
import {
  PasswordlessResponse,
  PhoneVerificationChannel,
  Purpose,
} from '../../managers/sso';
import VerifyEmailView from '../VerifyEmailView';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import { FORGOT_PASSWORD_FLOW, ROUTES } from '../../constants/routes';
import ScreenTransition, {
  TransitionType,
  ScreenType,
} from '../../utils/ScreenTransition';
import {
  EmailLoginMode,
  LoginPageProps,
  MobileLoginType,
  PasswordlessLoginResult,
  SheetLoginSuccessData,
} from '../../widgets/LoginView/types';
import type { TwoFaLoginData } from '../../hooks/useEmailLoginSubmit';
import { useLocalLogout, useUser } from '../../hooks/account';
import { isProfileComplete } from '../../utils/signUpFormUtils';

export type LoginBottomSheetProps = {
  visible: boolean;
  onClose: () => void;
  onForgotPasswordClick?: () => void;
  onLoginSuccess?: (data: SheetLoginSuccessData) => void;
};

// Define screen configuration array outside component for better performance
const screenConfigs = [
  { type: ScreenType.LOGIN, transition: TransitionType.FADE_SLIDE },
  { type: ScreenType.FORGOT_PASSWORD, transition: TransitionType.SLIDE_RIGHT },
  { type: ScreenType.SIGNUP, transition: TransitionType.SLIDE_RIGHT },
  { type: ScreenType.VERIFY_ACCOUNT, transition: TransitionType.SLIDE_RIGHT },
  { type: ScreenType.VERIFY_OTP, transition: TransitionType.SLIDE_RIGHT },
  { type: ScreenType.VERIFY_EMAIL_OTP, transition: TransitionType.SLIDE_RIGHT },
];

// LoginView and SignUpView have no side padding of their own (the full-screen
// pages pad them); the other screens pad themselves.
const UNPADDED_SCREENS = [ScreenType.LOGIN, ScreenType.SIGNUP];

const LoginBottomSheet: React.FC<LoginBottomSheetProps> = ({
  visible,
  onClose,
  onForgotPasswordClick,
  onLoginSuccess,
}) => {
  const router = useRouter();
  const logLoginSuccess = useLogSheetLoginSuccess('LoginBottomSheet');
  const { refetch: refetchUser } = useUser('LoginBottomSheet');
  const { mutateAsync: localLogout } = useLocalLogout();
  const [currentScreen, setCurrentScreen] = useState<ScreenType>(
    ScreenType.LOGIN,
  );
  const [mobileLoginData, setMobileLoginData] =
    useState<MobileLoginType | null>(null);

  // Drives the VERIFY_EMAIL_OTP screen: forgot password, passwordless login
  // and sign-up, and email 2FA all verify an emailed code.
  const [emailOtp, setEmailOtp] = useState<{
    email?: string;
    purpose: Purpose;
    cancelDeletion?: boolean;
  } | null>(null);

  // How LOGIN opens when the email code screen sends the user to another
  // sign-in method; the sheets have no routes for those options.
  const [loginEntry, setLoginEntry] = useState<Pick<
    LoginPageProps,
    'emailLoginMode' | 'prefilledEmail' | 'initialLoginMethod'
  > | null>(null);

  // Signed in by an emailed code with a profile missing mandatory fields (a new
  // user): the form must be completed before the host is told about the login.
  const [incompleteSignup, setIncompleteSignup] = useState<{
    email?: string;
    session: SheetLoginSuccessData;
  } | null>(null);
  // Set when that form saved the profile but the new number needs verifying.
  const [signupPhone, setSignupPhone] = useState<{
    phoneNumber: string;
    dialingCode: string;
    availableChannels: PhoneVerificationChannel[];
  } | null>(null);

  // The keep-account dialog lives in LoginView, so the sheet stays mounted but
  // hidden behind it; closing the sheet would lose the dialog and its retry.
  const [isKeepAccountOpen, setIsKeepAccountOpen] = useState(false);

  const handleForgotPasswordEmailSent = useCallback((email: string) => {
    setEmailOtp({ email, purpose: Purpose.FORGOT_PASSWORD });
    setCurrentScreen(ScreenType.VERIFY_EMAIL_OTP);
  }, []);

  const handlePasswordlessLoginSuccess = useCallback(
    (data: PasswordlessLoginResult) => {
      setEmailOtp({
        email: data.email,
        purpose: Purpose.EMAIL_OTP_VERIFICATION_LOGIN,
        cancelDeletion: data.cancelDeletion,
      });
      setCurrentScreen(ScreenType.VERIFY_EMAIL_OTP);
    },
    [],
  );

  const handlePasswordlessSignUpSuccess = useCallback(
    (data: PasswordlessResponse) => {
      setEmailOtp({
        email: data.email,
        purpose: Purpose.EMAIL_OTP_VERIFICATION_SIGNUP,
      });
      setCurrentScreen(ScreenType.VERIFY_EMAIL_OTP);
    },
    [],
  );

  const handleTwoFactorRequired = useCallback(({ email }: TwoFaLoginData) => {
    setEmailOtp({ email, purpose: Purpose.LOGIN_2FA });
    setCurrentScreen(ScreenType.VERIFY_EMAIL_OTP);
  }, []);

  const handleForgotPasswordClick = () => {
    setCurrentScreen(ScreenType.FORGOT_PASSWORD);
    onForgotPasswordClick?.();
  };

  const handleBackToLogin = () => {
    setCurrentScreen(ScreenType.LOGIN);
    setEmailOtp(null);
    setLoginEntry(null);
    setMobileLoginData(null); // Reset mobile login data when going back
  };

  const handleClose = () => {
    setCurrentScreen(ScreenType.LOGIN);
    setEmailOtp(null);
    setLoginEntry(null);
    setIncompleteSignup(null);
    setSignupPhone(null);
    setMobileLoginData(null); // Reset mobile login data when closing
    onClose();
  };

  const handleSignUpClick = () => {
    setCurrentScreen(ScreenType.SIGNUP);
  };

  const handleProceedToSignUpForm = () => {
    setCurrentScreen(ScreenType.VERIFY_ACCOUNT);
  };

  const handleMobileLoginPress = useCallback((data: MobileLoginType) => {
    setMobileLoginData(data);
  }, []);

  const handleProceedToVerifyOtp = useCallback(() => {
    setCurrentScreen(ScreenType.VERIFY_OTP);
  }, []);

  const handleForgotPasswordOtpVerified = () => {
    handleClose();
    router.push({
      pathname: ROUTES.ACCOUNT_CREATE_NEW_PASSWORD,
      params: { flow: FORGOT_PASSWORD_FLOW },
    });
  };

  // Reset to LOGIN screen when modal opens (only when transitioning from closed to open)
  const prevVisibleRef = useRef(visible);
  useEffect(() => {
    if (visible && !prevVisibleRef.current) {
      setCurrentScreen(ScreenType.LOGIN);
      setMobileLoginData(null);
      setEmailOtp(null);
      setLoginEntry(null);
      setIncompleteSignup(null);
      setSignupPhone(null);
    }
    prevVisibleRef.current = visible;
  }, [visible]);

  const dialingCodeForOtp = mobileLoginData?.dialingCode?.startsWith('+')
    ? mobileLoginData.dialingCode.replace('+', '')
    : (mobileLoginData?.dialingCode ?? '');

  const openLoginWith = (entry: NonNullable<typeof loginEntry>) => {
    handleBackToLogin();
    setLoginEntry(entry);
  };

  const emailOtpNavigation = {
    onSignInWithPassword: () =>
      openLoginWith({
        emailLoginMode: EmailLoginMode.PASSWORD,
        prefilledEmail: emailOtp?.email,
      }),
    onSignInWithPhoneNumber: () =>
      openLoginWith({ initialLoginMethod: 'mobile' }),
    onSignInWithPasskey: handleBackToLogin,
    onSignUpWithDiffEmail: () => {
      handleBackToLogin();
      setCurrentScreen(ScreenType.SIGNUP);
    },
  };

  const handleEmailOtpSignedIn = async (session: SheetLoginSuccessData) => {
    // Only a fetched profile can send the user to the form: a failed request
    // must not treat an existing user as new.
    const user = (await refetchUser()).data;
    if (user && !isProfileComplete(user)) {
      setIncompleteSignup({ email: emailOtp?.email, session });
      setCurrentScreen(ScreenType.VERIFY_ACCOUNT);
      return;
    }
    void logLoginSuccess('email');
    onLoginSuccess?.(session);
    handleClose();
  };

  const handleIncompleteSignupDone = () => {
    onLoginSuccess?.(incompleteSignup?.session);
    handleClose();
  };

  // Same as the full-screen form: leaving without the details signs out.
  const handleIncompleteSignupCancel = async () => {
    await localLogout();
    handleClose();
  };

  const renderCurrentScreen = () => {
    switch (currentScreen) {
      case ScreenType.LOGIN:
        return (
          <LoginView
            {...defaultProps}
            {...loginEntry}
            onCloseIconClick={handleClose}
            onForgotPasswordClick={handleForgotPasswordClick}
            onSignUpClick={handleSignUpClick}
            onLoginSuccess={(data) => {
              void logLoginSuccess((data as { method?: string }).method);
              onLoginSuccess?.(data);
              onClose();
            }}
            onMobileLoginPress={handleMobileLoginPress}
            onMobileLoginSuccess={handleProceedToVerifyOtp}
            onPasswordLessEmailLoginSuccess={handlePasswordlessLoginSuccess}
            onTwoFactorRequired={handleTwoFactorRequired}
            onKeepAccountVisibleChange={setIsKeepAccountOpen}
          />
        );
      case ScreenType.FORGOT_PASSWORD:
        return (
          <ForgotPasswordView
            {...forgotPasswordDefaultProps}
            onSendEmailSuccess={handleForgotPasswordEmailSent}
            onBackPress={handleBackToLogin}
          />
        );
      case ScreenType.SIGNUP:
        return (
          <SignUpView
            {...signUpDefaultProps}
            onCloseIconClick={handleBackToLogin}
            onPasswordLessEmailSignUpSuccess={handlePasswordlessSignUpSuccess}
            onProceedToPersonalDetails={handleProceedToSignUpForm}
          />
        );
      case ScreenType.VERIFY_ACCOUNT:
        return (
          <SignUpFormView
            {...signUpFormDefaultProps}
            onCloseIconClick={
              incompleteSignup
                ? handleIncompleteSignupCancel
                : handleBackToLogin
            }
            email={incompleteSignup?.email}
            isIncompleteSignup={!!incompleteSignup}
            onNavigateToVerifyOtp={(data) => {
              setSignupPhone(data);
              setCurrentScreen(ScreenType.VERIFY_OTP);
            }}
            onSignUpSuccess={
              incompleteSignup ? handleIncompleteSignupDone : undefined
            }
          />
        );
      case ScreenType.VERIFY_OTP:
        if (signupPhone) {
          return (
            <VerifyPhoneView
              purpose={Purpose.PHONE_VERIFICATION}
              phoneNumber={signupPhone.phoneNumber}
              dialingCode={signupPhone.dialingCode}
              availableChannels={signupPhone.availableChannels}
              onVerified={handleIncompleteSignupDone}
              onUpdateMobileNumber={() => {
                setSignupPhone(null);
                setCurrentScreen(ScreenType.VERIFY_ACCOUNT);
              }}
              showTitle={true}
            />
          );
        }
        return (
          <VerifyPhoneView
            purpose={Purpose.LOGIN_2FA}
            phoneNumber={mobileLoginData?.mobileNumber ?? ''}
            dialingCode={dialingCodeForOtp}
            onVerified={(result) => {
              void logLoginSuccess('mobile');
              onLoginSuccess?.(result?.session);
              handleClose();
            }}
            onUpdateMobileNumber={handleBackToLogin}
            showTitle={true}
          />
        );
      case ScreenType.VERIFY_EMAIL_OTP:
        return (
          <VerifyEmailView
            email={emailOtp?.email}
            purpose={emailOtp?.purpose ?? Purpose.FORGOT_PASSWORD}
            cancelDeletion={emailOtp?.cancelDeletion}
            navigationHandlers={emailOtpNavigation}
            onVerified={async (result) => {
              if (emailOtp?.purpose === Purpose.FORGOT_PASSWORD) {
                handleForgotPasswordOtpVerified();
                return;
              }
              // Verifying the code has persisted the session, as for mobile OTP.
              await handleEmailOtpSignedIn(result?.session);
            }}
          />
        );
      default:
        return null;
    }
  };

  // Padding belongs to the screen, not the shared container, so it moves
  // with the screen during the slide instead of snapping on the parent.
  const renderScreen = () =>
    UNPADDED_SCREENS.includes(currentScreen) ? (
      <View style={styles.screenPadding}>{renderCurrentScreen()}</View>
    ) : (
      renderCurrentScreen()
    );

  {
    /*
        TODO: Replace this Modal with BottomSheetView
        from @airasia-phoenix/react-native once it is supported on web.
      */
  }
  return (
    <Modal
      transparent
      visible={visible}
      animationType="none"
      onRequestClose={
        incompleteSignup ? handleIncompleteSignupCancel : handleClose
      }
    >
      {/* Provider inside the Modal so nested sheets (country list) render above it. */}
      <BottomSheetModalProvider>
        {/* Overlay behind sheet */}
        {/* A tap outside must not abandon the sign-up form half way. */}
        <TouchableWithoutFeedback
          onPress={incompleteSignup ? undefined : onClose}
        >
          <View
            style={[styles.overlay, isKeepAccountOpen && styles.overlayHidden]}
          />
        </TouchableWithoutFeedback>

        <View
          style={[
            styles.bottomSheetWrapper,
            isKeepAccountOpen && styles.bottomSheetWrapperHidden,
          ]}
        >
          <ScrollView
            contentContainerStyle={styles.scrollContent}
            keyboardShouldPersistTaps="handled"
          >
            <View style={styles.bottomSheet}>
              <View style={styles.screenContainer}>
                {screenConfigs.map(({ type, transition }) => (
                  <ScreenTransition
                    key={type}
                    isVisible={currentScreen === type}
                    transitionType={transition}
                  >
                    {currentScreen === type && renderScreen()}
                  </ScreenTransition>
                ))}
              </View>
            </View>
          </ScrollView>
        </View>
      </BottomSheetModalProvider>
    </Modal>
  );
};

export default LoginBottomSheet;

const styles = StyleSheet.create((pd) => ({
  overlay: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    backgroundColor: getColor(pd.sys.colour.overlay),
  },
  // The keep-account dialog brings its own dimmed backdrop.
  overlayHidden: {
    backgroundColor: 'transparent',
  },
  bottomSheetWrapperHidden: {
    display: 'none',
  },
  bottomSheetWrapper: {
    backgroundColor: getColor(pd.sys.colour.bg_primary),
    width: '100%',
    maxHeight: '90%',
    minHeight: '40%',
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
  },
  scrollContent: {
    flexGrow: 1,
  },
  bottomSheet: {
    paddingVertical: pd.ref.space.spacing40.px,
    backgroundColor: getColor(pd.sys.colour.bg_primary),
    width: '100%',
  },
  screenContainer: {
    position: 'relative',
    width: '100%',
  },
  screenPadding: {
    paddingHorizontal: pd.ref.space.spacing40.px,
  },
}));
